import { useState } from 'react';
import { useMinicrmMigrationStore } from '../../../store/minicrmMigrationStore';
import type { SchoolProposal, SchoolDecisionPayload } from '../../../types/minicrmMigration';
import { OrgSearchModal } from './OrgSearchModal';

/** Human-readable summary of an already-decided school row. */
function decidedSummary(s: SchoolProposal): { text: string; tone: 'ok' | 'drop' } {
  if (s.decision === 'CREATE_NEW_ORG') {
    return { text: `✓ create new: ${s.createNewOrgName || s.miniSchoolString}`, tone: 'ok' };
  }
  if (s.decision === 'DROPPED') {
    return { text: '✗ dropped', tone: 'drop' };
  }
  // APPROVED / CHANGED — name the resolved org (hydrated; falls back to a candidate).
  const org = s.resolvedOrg ?? s.candidates.find((c) => c.id === s.resolvedOrgId) ?? null;
  const name = org?.name ?? '(unknown org)';
  const city = org?.city;
  return { text: `✓ resolved to ${name}${city ? ` (${city})` : ''}`, tone: 'ok' };
}

export function SchoolReviewTable() {
  const { schools, decideSchool } = useMinicrmMigrationStore();
  const [searchFor, setSearchFor] = useState<SchoolProposal | null>(null);
  const [createFor, setCreateFor] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  // Decided rows the reviewer has clicked "Change" on — flipped back to controls.
  const [changing, setChanging] = useState<Set<string>>(new Set());

  if (schools.length === 0) {
    return <div className="p-4 text-sm text-gray-500">No schools need review. 🎉</div>;
  }

  const decide = (s: SchoolProposal, payload: SchoolDecisionPayload) => {
    decideSchool(s.id, payload);
    setCreateFor((c) => (c === s.id ? null : c));
    setChanging((prev) => { const next = new Set(prev); next.delete(s.id); return next; });
  };

  const cancelChange = (id: string) => {
    setCreateFor((c) => (c === id ? null : c));
    setChanging((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const renderControls = (s: SchoolProposal, isChanging: boolean) => (
    <div className="space-y-1.5">
      {s.candidates.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-3">
          <span className="text-gray-700">
            {c.name} · {[c.city, c.state].filter(Boolean).join(', ') || '—'} · <span className="text-gray-400">{c.source} · {c.rosterSize} roster</span>
          </span>
          <button type="button" className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100" onClick={() => decide(s, { decision: 'APPROVED', resolvedOrgId: c.id })}>Use this</button>
        </div>
      ))}
      {s.candidates.length === 0 && <div className="text-xs text-gray-400">(no candidate matched)</div>}
      <div className="flex gap-2 pt-1">
        <button type="button" className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => setSearchFor(s)}>Search &amp; pick…</button>
        <button type="button" className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => { setCreateFor(s.id); setNewName(s.miniSchoolString); }}>Create new</button>
        <button type="button" className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-700 hover:bg-rose-100" onClick={() => decide(s, { decision: 'DROPPED' })}>Drop</button>
        {isChanging && <button type="button" className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => cancelChange(s.id)}>Cancel</button>}
      </div>
      {createFor === s.id && (
        <div className="flex gap-2 pt-1">
          <input className="border rounded px-2 py-1 text-xs flex-1" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New org name" />
          <button type="button" className="text-xs px-2 py-1 rounded bg-emerald-600 text-white" onClick={() => decide(s, { decision: 'CREATE_NEW_ORG', createNewOrgName: newName.trim() || s.miniSchoolString })}>Create</button>
          <button type="button" className="text-xs px-2 py-1 rounded bg-gray-100" onClick={() => setCreateFor(null)}>Cancel</button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <table className="w-full text-sm border-collapse">
        <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
          <tr>
            <th className="text-left px-3 py-2">Mini school</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Coaches</th>
            <th className="text-right px-3 py-2">Activities</th>
            <th className="text-left px-3 py-2">Candidates / action</th>
          </tr>
        </thead>
        <tbody>
          {schools.map((s) => {
            const isChanging = changing.has(s.id);
            const showControls = s.decision === 'PENDING' || isChanging;
            return (
              <tr key={s.id} className="border-t align-top">
                <td className="px-3 py-2 font-medium">{s.miniSchoolString || '(blank)'}</td>
                <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${s.status === 'AMBIGUOUS' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>{s.status}</span></td>
                <td className="px-3 py-2 text-right">{s.contactCount}</td>
                <td className="px-3 py-2 text-right">{s.activityCount}</td>
                <td className="px-3 py-2">
                  {showControls ? (
                    renderControls(s, isChanging)
                  ) : (
                    (() => {
                      const { text, tone } = decidedSummary(s);
                      return (
                        <div className="flex items-center justify-between gap-3">
                          <span className={tone === 'drop' ? 'text-rose-600' : 'text-emerald-700'}>{text}</span>
                          <button type="button" className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => setChanging((prev) => new Set(prev).add(s.id))}>Change</button>
                        </div>
                      );
                    })()
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {searchFor && (
        <OrgSearchModal
          miniSchoolString={searchFor.miniSchoolString}
          onClose={() => setSearchFor(null)}
          onPick={(orgId) => { decide(searchFor, { decision: 'CHANGED', resolvedOrgId: orgId }); setSearchFor(null); }}
        />
      )}
    </>
  );
}
